import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, withQuery, money, date, dateTime } from '../api.js';
import ProductLookup from '../components/ProductLookup.jsx';

/**
 * „Чакат продажба“ (20.10.2026) — изписаните в Деливери ремонти и зареждания,
 * които още не са продадени. Заменя гаранционните карти на хартия на бюрото:
 * клиентът идва след дни или месеци, операторът го намира тук и с един клик
 * пуска продажбата.
 *
 * Редът е клиент (обектът в Деливери); при клик отдолу излизат услугите му
 * с отметки — може 2 касети на една фактура, а другите 3 на друга. Клик на
 * услугата отваря картона ѝ: какво е правено, SMS-ите и нов SMS.
 */
const IZGLEDI = [
  ['chaka', 'Чакат'],
  ['brak', 'Брак'],
  ['mahnata', 'Махнати'],
];
const VID = { repair: 'Ремонт', refill: 'Зареждане' };
// На тези не се праща SMS (Калин, 20.10.2026) — касетата пътува сама.
const SMS_STATUS = { sent: 'изпратен', delivered: 'доставен', failed: 'неуспешен', pending: 'чака' };
const BEZ_SMS = { courier: 'за разнос', with_printer: 'връща се с принтера' };

function kakvo(g) {
  const ch = [];
  if (g.zarezhdania) ch.push(`${g.zarezhdania} ${g.zarezhdania === 1 ? 'касета' : 'касети'}`);
  if (g.remonti) ch.push(`${g.remonti} ${g.remonti === 1 ? 'принтер' : 'принтера'}`);
  return ch.join(', ');
}

/** Търсене по клиент, лице, телефон, модел, сериен номер — без интервали и тирета. */
const norm = (s) => String(s ?? '').toLowerCase().replace(/[\s\-+().]/g, '');
function sapada(g, q) {
  if (!q) return true;
  const t = norm(q);
  const poleta = [g.klient, g.firma, g.litse, g.telefon,
    ...g.uslugi.flatMap((u) => [u.predmet, u.serien, u.litse, u.telefon, u.nomer])];
  return poleta.some((p) => norm(p).includes(t));
}

export default function ChakatProdazhba() {
  const [izgled, setIzgled] = useState('chaka');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [q, setQ] = useState('');
  const [selKey, setSelKey] = useState(null);
  const [reload, setReload] = useState(0);
  const [karton, setKarton] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get(withQuery('/api/chakat-prodazhba', { izgled }))
      .then((r) => {
        if (cancelled) return;
        setData(r);
        window.dispatchEvent(new CustomEvent('chakat-broi', { detail: r.broi?.chaka ?? null }));
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [izgled, reload]);

  const redove = useMemo(() => (data?.redove ?? []).filter((g) => sapada(g, q)), [data, q]);
  const izbranaGrupa = redove.find((g) => g.kluch === selKey) ?? null;

  function obnovi(msg) {
    if (msg) setNotice(msg);
    setReload((n) => n + 1);
  }

  return (
    <>
      <div className="page-head">
        <h1>Чакат продажба</h1>
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>
          изписани в Деливери от {data ? date(data.otData) : '…'} и непродадени
          {data?.uslugi != null && ` · ${redove.length} клиента, ${data.uslugi} услуги`}
        </span>
      </div>

      <div className="tabs">
        {IZGLEDI.map(([k, l]) => (
          <button key={k} className={`tab ${izgled === k ? 'active' : ''}`}
            onClick={() => { setIzgled(k); setSelKey(null); setNotice(null); }}>
            {l}{data?.broi?.[k] != null ? ` (${data.broi[k]})` : ''}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      <div className="toolbar">
        <label className="field" style={{ minWidth: 320 }}>
          Търсене
          <input type="search" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="клиент, лице, телефон, модел, сериен номер" />
        </label>
      </div>

      <div className="card">
        {/* 25 реда видими наведнъж (ред ≈ 21px при сбитата таблица). */}
        <div className="table-wrap" style={{ maxHeight: 556 }}>
          <table className="compact">
            <thead>
              <tr>
                <th>Клиент</th>
                <th>Лице за контакт</th>
                <th>Телефон</th>
                <th>Какво</th>
                <th>Приет</th>
                <th>Изписан</th>
                <th className="num">Чака</th>
                <th className="num">Сума</th>
                <th className="num">SMS</th>
              </tr>
            </thead>
            <tbody>
              {redove.map((g) => {
                const nai = g.uslugi.reduce((m, u) => (!m || u.priet < m ? u.priet : m), null);
                return (
                  <tr key={g.kluch} className={`clickable ${g.kluch === selKey ? 'open' : ''}`}
                    onClick={() => setSelKey(g.kluch)}>
                    <td>
                      <strong>{g.klient}</strong>
                      {g.firma && g.firma !== g.klient && <span className="muted"> · {g.firma}</span>}
                    </td>
                    <td className="muted">{g.litse ?? '—'}</td>
                    <td className="muted">{g.telefon ?? '—'}</td>
                    <td>{kakvo(g)}</td>
                    <td className="muted">{date(nai)}</td>
                    {/* С часа — в рамките на деня подредбата е по него. */}
                    <td className="muted">{dateTime(g.naiStaroIzpisan)}</td>
                    <td className="num">
                      <span className={g.chakaDni > 30 ? 'badge danger' : g.chakaDni > 7 ? 'badge warn' : 'badge'}>
                        {g.chakaDni} д.
                      </span>
                    </td>
                    <td className="num">{g.suma ? money(g.suma) : '—'}{!g.sumaPylna && <span className="muted" title="за част от услугите няма цена"> +?</span>}</td>
                    <td className="num">{g.sms || '—'}</td>
                  </tr>
                );
              })}
              {data && redove.length === 0 && (
                <tr><td colSpan={9} className="empty">{q ? 'Няма съвпадения' : 'Няма нищо тук'}</td></tr>
              )}
              {!data && !error && <tr><td colSpan={9} className="empty">Зареждане от Деливери…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {izbranaGrupa && (
        <GrupaPanel key={izbranaGrupa.kluch + reload} g={izbranaGrupa} izgled={izgled}
          prichini={data?.prichini ?? {}} onKarton={setKarton} onDone={obnovi} onError={setError}
          onClose={() => setSelKey(null)} />
      )}

      {karton && (
        <UslugaKarton u={karton} onClose={() => setKarton(null)}
          onSent={(msg) => { setKarton(null); obnovi(msg); }} />
      )}
    </>
  );
}

/** Услугите на избрания клиент — отметки, продажба, свързване, махане. */
function GrupaPanel({ g, izgled, prichini, onKarton, onDone, onError, onClose }) {
  // За кое зареждане е отворен изборът на услуга (ref) — 30.10.2026.
  const [izborUsluga, setIzborUsluga] = useState(null);
  async function zapomniUsluga(u, productId, name) {
    try {
      await api.post('/api/chakat-prodazhba/usluga-za-model', { model: u.predmet, productId });
      setIzborUsluga(null);
      onDone(productId
        ? `Запомнено: ${u.predmet} → ${name}.`
        : `Забравена услугата за ${u.predmet}.`);
    } catch (e) {
      onError(e.message);
    }
  }
  const navigate = useNavigate();
  // Зареждане без манипулация не се избира само — виж bezManipulacia на сървъра.
  const [izbr, setIzbr] = useState(() => new Set(g.uslugi.filter((u) => !u.bezManipulacia).map((u) => u.ref)));
  const [partnerId, setPartnerId] = useState(g.partneri.length === 1 ? String(g.partneri[0].id) : '');
  const [busy, setBusy] = useState(false);
  const [kandidati, setKandidati] = useState([]);
  const [prichina, setPrichina] = useState('');
  const [belezhka, setBelezhka] = useState('');
  // Обект без фирма в Тефтер (23.10.2026) — фирмата се избира тук, не в продажбата.
  const bezFirma = g.partneri.length === 0 && !g.razni;
  const [firmaQ, setFirmaQ] = useState('');
  const [firmaHits, setFirmaHits] = useState([]);
  const [firma, setFirma] = useState(null);
  useEffect(() => {
    if (!bezFirma || firmaQ.trim().length < 2 || (firma && firma.name === firmaQ)) { setFirmaHits([]); return undefined; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/partners', { q: firmaQ, type: 'client', limit: 40 }))
        .then((r) => setFirmaHits(r.items ?? [])).catch(() => setFirmaHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [bezFirma, firmaQ, firma]);

  const izbrani = g.uslugi.filter((u) => izbr.has(u.ref));
  const zaSms = izbrani.filter((u) => !(u.vid === 'refill' && BEZ_SMS[u.nachinDostavka]));
  const [smsOtvoren, setSmsOtvoren] = useState(false);
  const [smsTel, setSmsTel] = useState(g.telefon ?? '');
  const [smsTekst, setSmsTekst] = useState('');
  const suma = izbrani.reduce((s, u) => s + (u.cena ?? 0), 0);
  const refs = izbrani.map((u) => u.ref);

  // Продажби на клиента от най-старото приемане насам — „вероятно продадена“.
  useEffect(() => {
    // При „Разни“ — не: там са всички продажби на случайни клиенти и
    // предположенията нищо не казват (решение на Калин, 20.10.2026).
    if (izgled !== 'chaka' || !g.partneri.length || g.razni) { setKandidati([]); return; }
    const ot = g.uslugi.reduce((m, u) => (!m || u.priet < m ? u.priet : m), null);
    api.get(withQuery('/api/chakat-prodazhba/kandidati', {
      partneri: g.partneri.map((p) => p.id).join(','), ot: String(ot).slice(0, 10),
    })).then((r) => setKandidati(r.items)).catch(() => setKandidati([]));
  }, [g, izgled]);

  function obarni(ref) {
    setIzbr((s) => { const n = new Set(s); if (n.has(ref)) n.delete(ref); else n.add(ref); return n; });
  }

  async function deystvie(fn) {
    setBusy(true);
    onError(null);
    try { await fn(); } catch (e) { onError(e.message); } finally { setBusy(false); }
  }

  const prodai = () => deystvie(async () => {
    if (g.partneri.length > 1 && !partnerId) throw new Error('Изберете на коя фирма да е продажбата');
    if (bezFirma && !firma) throw new Error('Изберете фирмата, на която да е продажбата');
    const r = await api.post('/api/chakat-prodazhba/prodai', {
      refs, partnerId: bezFirma ? firma.id : (partnerId ? Number(partnerId) : null),
    });
    // „Затвори“ след записа връща тук (03.11.2026).
    navigate(`/documents/${r.id}/continue`, { state: { otSpisak: window.location.pathname + window.location.search } });
  });
  const svarzhi = (doc) => deystvie(async () => {
    if (!window.confirm(`Да свържа ли ${refs.length} ${refs.length === 1 ? 'услуга' : 'услуги'} с продажба № ${doc.doc_number}? Те ще се водят продадени.`)) return;
    await api.post('/api/chakat-prodazhba/svarzhi', { refs, documentId: doc.id });
    onDone(`Свързани с продажба № ${doc.doc_number}.`);
  });
  const mahni = () => deystvie(async () => {
    if (!prichina) throw new Error('Изберете причина');
    await api.post('/api/chakat-prodazhba/mahni', { refs, reason: prichina, note: belezhka || null });
    onDone(`Махнати ${refs.length} от списъка — ${prichini[prichina]}.`);
  });
  const otvoriSms = () => {
    setSmsTekst(napomnyashtTekst(zaSms));
    setSmsOtvoren((v) => !v);
  };
  const pratiSms = () => deystvie(async () => {
    const r = await api.post('/api/chakat-prodazhba/napomni', {
      refs: zaSms.map((u) => u.ref), phone: smsTel || null, message: smsTekst,
    });
    setSmsOtvoren(false);
    onDone(`Напомнящ SMS до ${r.phone ?? smsTel}${r.status && r.status !== 'sent' ? ` — ${r.status}` : ' е изпратен'}.`);
  });
  const varni = () => deystvie(async () => {
    await api.post('/api/chakat-prodazhba/varni', { refs });
    onDone(`Върнати ${refs.length} в списъка.`);
  });

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head" style={{ display: 'flex', alignItems: 'center' }}>
        {g.klient}
        <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
          {[g.litse, g.telefon].filter(Boolean).join(' · ')}
        </span>
        {/* Затваря прегледа — остава само списъкът (28.10.2026). */}
        <button type="button" className="link" onClick={onClose} title="Затвори"
          style={{ marginLeft: 'auto', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
      </div>
      <div className="table-wrap" style={{ maxHeight: '40vh' }}>
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input type="checkbox" checked={izbr.size === g.uslugi.length}
                  onChange={(e) => setIzbr(e.target.checked ? new Set(g.uslugi.map((u) => u.ref)) : new Set())} />
              </th>
              <th>Вид</th>
              <th>Модел</th>
              <th>Сериен №</th>
              <th>Приет</th>
              <th>Изписан</th>
              <th>Извършено</th>
              <th className="num">Цена</th>
              <th className="num">SMS</th>
              {izgled === 'mahnata' && <th>Махната</th>}
            </tr>
          </thead>
          <tbody>
            {g.uslugi.map((u) => (
              <Fragment key={u.ref}>
              <tr className="clickable" onClick={() => onKarton(u)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={izbr.has(u.ref)} onChange={() => obarni(u.ref)} />
                </td>
                <td>{VID[u.vid]}</td>
                <td><strong>{u.predmet}</strong></td>
                <td className="muted">{u.serien ?? '—'}</td>
                <td className="muted">{date(u.priet)}</td>
                <td className="muted">{date(u.izpisan)}</td>
                <td className="muted" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={u.izvarsheno ?? ''}>
                  {u.bezManipulacia
                    ? <span className="badge warn" title="Нищо не е отметнато в Деливери — касетата е върната пълна, не се таксува">без манипулация</span>
                    : (u.izvarsheno ?? '—')}
                </td>
                <td className="num" title={u.cenaOtkade}>
                  {u.cena != null ? money(u.cena) : <span className="muted">{u.cenaOtkade}</span>}
                </td>
                <td className="num">{u.sms?.length || '—'}</td>
                {izgled === 'mahnata' && (
                  <td className="muted">{u.mahnata?.prichina}{u.mahnata?.belezhka ? ` · ${u.mahnata.belezhka}` : ''}</td>
                )}
              </tr>
              {/* Какво влиза в продажбата — услуга, части, подаръци (29.10.2026). */}
              {(u.redove ?? []).map((r, i) => (
                <tr key={`${u.ref}-${i}`} className="clickable" onClick={() => onKarton(u)} style={{ fontSize: '0.9em' }}>
                  <td />
                  <td colSpan={6} className="muted" style={{ paddingLeft: 16 }}
                    onClick={(e) => { if (izborUsluga === u.ref) e.stopPropagation(); }}>
                    {i === 0 && u.vid === 'refill' && izborUsluga === u.ref ? (
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        ↳
                        <ProductLookup value={null} label="" placeholder="услугата за зареждане — име или бърз код"
                          style={{ minWidth: 320 }} onPick={(id, name) => id && zapomniUsluga(u, id, name)} />
                        {u.uslugaZapomnena && (
                          <button type="button" className="link" onClick={() => zapomniUsluga(u, null)}>забрави</button>
                        )}
                        <button type="button" className="link" onClick={() => setIzborUsluga(null)}>отказ</button>
                      </span>
                    ) : (
                      <>
                        ↳ {r.ime}{r.kol !== 1 ? ` × ${r.kol}` : ''}
                        {r.podarak && <span className="badge" style={{ marginLeft: 6 }}>подарък</span>}
                        {r.belezhka && <span style={{ marginLeft: 6, opacity: 0.75 }}>· {r.belezhka}</span>}
                        {/* Услугата за модела — избира се веднъж и се помни (30.10.2026). */}
                        {i === 0 && u.vid === 'refill' && izgled === 'chaka' && (
                          <button type="button" className={u.uslugaId ? 'link' : 'link error'}
                            style={{ marginLeft: 8, fontWeight: u.uslugaId ? 400 : 600 }}
                            title={u.uslugaZapomnena ? 'Запомнена за този модел — смени' : 'Изберете услугата — ще се помни за този модел'}
                            onClick={(e) => { e.stopPropagation(); setIzborUsluga(u.ref); }}>
                            {u.uslugaId ? (u.uslugaZapomnena ? 'запомнена · смени' : 'смени') : 'избери услуга'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                  <td className="num muted">{r.sDds != null ? money(r.sDds) : '—'}</td>
                  <td />
                  {izgled === 'mahnata' && <td />}
                </tr>
              ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Изпратените SMS-и на клиента (23.10.2026) — по всички услуги в групата, най-новите горе. */}
      {(() => {
        // От сървъра — всички SMS-и до клиента, и груповите, закачени към друга касета.
        const smsi = g.smsKlient ?? [];
        return (
          <div className="card-body" style={{ paddingBottom: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Изпратени SMS-и ({smsi.length})</div>
            {smsi.length === 0 ? <div className="muted">Няма изпратени</div> : (
              <table className="compact">
                <tbody>
                  {smsi.map((s, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: 'nowrap' }}>{dateTime(s.sentAt)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{s.phone}</td>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{s.predmet}</td>
                      <td className="muted">{SMS_STATUS[s.status] ?? s.status}</td>
                      <td className="muted" style={{ maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={s.message}>{s.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}

      <div className="card-body">
        {izgled === 'mahnata' ? (
          <div className="toolbar" style={{ marginTop: 0 }}>
            <div className="spacer" />
            <button disabled={busy || !refs.length} onClick={varni}>Върни в списъка ({refs.length})</button>
          </div>
        ) : (
          <>
            <div className="toolbar" style={{ marginTop: 0, alignItems: 'flex-end' }}>
              {g.partneri.length > 1 && (
                <label className="field">
                  Фирма за продажбата
                  <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                    <option value="">— изберете —</option>
                    {g.partneri.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
              )}
              {bezFirma && (
                <label className="field" style={{ position: 'relative', minWidth: 280 }}>
                  Фирма за продажбата (обектът не е свързан — ще се свърже)
                  <input value={firmaQ} autoComplete="off" placeholder="име или ЕИК"
                    onChange={(e) => { setFirmaQ(e.target.value); setFirma(null); }} />
                  {firmaHits.length > 0 && (
                    <div className="suggest">
                      {firmaHits.map((p) => (
                        <div key={p.id} className="suggest-item"
                          onMouseDown={(e) => { e.preventDefault(); setFirma(p); setFirmaQ(p.name); setFirmaHits([]); }}>
                          {p.name} <span className="muted">{p.eik ?? ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </label>
              )}
              <div className="spacer" />
              <button disabled={busy || !zaSms.length} onClick={otvoriSms}
                title={!zaSms.length && refs.length ? 'Избраните са за разнос или с принтера — без SMS' : 'SMS, че клиентът има готово за вземане'}>
                Напомни с SMS
              </button>
              <span className="muted">избрани {refs.length} · {money(suma)}</span>
              <button className="primary" disabled={busy || !refs.length || (bezFirma && !firma)} onClick={prodai}>
                Продай избраните ({refs.length})
              </button>
            </div>

            {smsOtvoren && (
              <div className="toolbar" style={{ marginTop: 12, alignItems: 'flex-end' }}>
                <label className="field" style={{ width: 160 }}>
                  Телефон
                  <input value={smsTel} autoComplete="off" onChange={(e) => setSmsTel(e.target.value)} />
                </label>
                <label className="field" style={{ flex: 1, minWidth: 260 }}>
                  Напомнящ SMS{zaSms.length < refs.length ? ` (без ${refs.length - zaSms.length} за разнос/с принтера)` : ''}
                  <textarea rows={2} value={smsTekst} onChange={(e) => setSmsTekst(e.target.value)} />
                </label>
                <button className="primary" disabled={busy || smsTekst.trim().length < 10} onClick={pratiSms}>
                  {busy ? 'Изпращане…' : 'Изпрати SMS'}
                </button>
                <button type="button" className="link" onClick={() => setSmsOtvoren(false)}>отказ</button>
              </div>
            )}

            {kandidati.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  Вероятно вече продадени — продажби с услуга на този клиент след приемането, пуснати на ръка.
                  Отметнете услугите горе и свържете:
                </div>
                <table className="compact">
                  <tbody>
                    {kandidati.map((k) => (
                      <tr key={k.id}>
                        <td><Link to={`/documents/${k.id}`}>№ {k.doc_number}</Link></td>
                        <td className="muted">{date(k.doc_date)}</td>
                        <td className="muted" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={k.uslugi}>{k.uslugi}</td>
                        <td className="num">{money(k.total)}</td>
                        <td className="muted">{Number(k.svarzani) ? `вече свързана с ${k.svarzani}` : ''}</td>
                        <td>
                          <button type="button" className="link" disabled={busy || !refs.length}
                            onClick={() => svarzhi(k)}>Свържи избраните ({refs.length})</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="toolbar" style={{ marginTop: 12, alignItems: 'flex-end' }}>
              <label className="field">
                Махни от списъка
                <select value={prichina} onChange={(e) => setPrichina(e.target.value)}>
                  <option value="">— причина —</option>
                  {Object.entries(prichini).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="field" style={{ flex: 1, minWidth: 200 }}>
                Бележка
                <input value={belezhka} autoComplete="off" onChange={(e) => setBelezhka(e.target.value)}
                  placeholder="по избор" />
              </label>
              <button disabled={busy || !refs.length || !prichina} onClick={mahni}>
                Махни избраните ({refs.length})
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Текстът на напомнянето — по това, какво чака клиента (23.10.2026). */
function napomnyashtTekst(uslugi) {
  const kaseti = uslugi.filter((u) => u.vid === 'refill').length;
  const printeri = uslugi.filter((u) => u.vid === 'repair').length;
  const chasti = [];
  if (kaseti) chasti.push(kaseti === 1 ? 'касетата' : `касетите (${kaseti} бр.)`);
  if (printeri) chasti.push(printeri === 1 ? 'принтерът' : `принтерите (${printeri} бр.)`);
  const mnozhestvo = kaseti + printeri > 1;
  const kakvo = chasti.join(' и ') || 'поръчката';
  const rod = !mnozhestvo && printeri ? 'готов' : 'готова';
  return `Здравейте, напомняме Ви, че ${kakvo} ${mnozhestvo ? 'Ви са готови и Ви очакват' : `Ви е ${rod} и Ви очаква`}`
    + ' в Доктор Принтер, ул. Батак 7, от 09:00 до 17:30 ч.';
}

/** Картонът на услугата — какво е правено, SMS-ите и нов SMS. */
function UslugaKarton({ u, onClose, onSent }) {
  const bezSms = u.vid === 'refill' ? BEZ_SMS[u.nachinDostavka] : null;
  const [phone, setPhone] = useState(u.telefon ?? '');
  const [message, setMessage] = useState(u.smsTekst ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function prati() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post('/api/chakat-prodazhba/sms', { ref: u.ref, phone: phone || null, message: message || null });
      onSent(`SMS до ${r.phone ?? phone}${r.status && r.status !== 'sent' ? ` — ${r.status}` : ' е изпратен'}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const R = ({ k, v }) => (v ? (
    <div style={{ display: 'flex', gap: 12, padding: '2px 0' }}>
      <span className="muted" style={{ minWidth: 130 }}>{k}</span><span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>
    </div>
  ) : null);

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 720 }}>
        <div className="card-head">
          {VID[u.vid]} · {u.predmet}
          {u.brak && <span className="badge danger" style={{ marginLeft: 8 }}>брак</span>}
          {u.nomer && <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>№ {u.nomer}</span>}
        </div>
        <div className="card-body">
          {error && <div className="error">{error}</div>}
          <R k="Клиент" v={[u.klient, u.firma && u.firma !== u.klient ? u.firma : null].filter(Boolean).join(' · ')} />
          <R k="Лице за контакт" v={u.litse} />
          <R k="Телефон" v={u.telefon} />
          <R k="Сериен №" v={u.serien} />
          <R k="Приет" v={date(u.priet)} />
          <R k="Изписан" v={date(u.izpisan)} />
          <R k="Техник" v={u.tehnik} />
          <R k="Оплакване" v={u.oplakvane} />
          <R k="Извършено" v={u.izvarsheno} />
          <R k="Бележка" v={u.zabelezhka} />
          <R k="Цена" v={u.cena != null ? `${money(u.cena)} (${u.cenaOtkade})` : u.cenaOtkade} />
          {u.vid === 'repair' && u.cenaVDeliveri && <R k="В Деливери" v={u.cenaVDeliveri} />}

          <div style={{ margin: '12px 0 4px', fontWeight: 600 }}>SMS-и ({u.sms?.length ?? 0})</div>
          {(u.sms ?? []).length === 0 ? <div className="muted">Няма изпратени</div> : (
            <table className="compact">
              <tbody>
                {u.sms.map((s, i) => (
                  <tr key={i}>
                    <td className="muted">{date(s.sentAt)}</td>
                    <td>{s.phone}</td>
                    <td className="muted">{s.status}</td>
                    <td className="muted" style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={s.message}>{s.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {bezSms ? (
            <div className="muted" style={{ marginTop: 14 }}>
              Не се изпраща SMS — зареждането е {bezSms}.
            </div>
          ) : (
            <>
              <div style={{ margin: '14px 0 4px', fontWeight: 600 }}>Нов SMS</div>
              <div className="toolbar" style={{ marginTop: 0, alignItems: 'flex-end' }}>
                <label className="field">
                  Телефон
                  <input value={phone} autoComplete="off" onChange={(e) => setPhone(e.target.value)} />
                </label>
              </div>
              <label className="field">
                Текст
                <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
              </label>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button onClick={onClose} disabled={busy}>Затвори</button>
          {!bezSms && (
            <button className="primary" onClick={prati} disabled={busy || !phone.trim()}>
              {busy ? 'Изпращане…' : 'Изпрати SMS'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
